import { html, View } from 'rune-ts';
import { font } from '@services/MarppleShop/shared/typography';
import klass from './PureInput.module.scss';

/* 외부에서 klass 주입하기 귀찮을 경우 여기에 추가 */
const VARIANT_KLASS_MAP = {
  pure: '',

  /* 스튜디오 */
  // 스튜디오 에디터 인풋
  studio_editor: klass.studio_editor_input,
  studio_editor_stock_option: `${klass.studio_editor_input} ${klass.stock_option}`,

  // 여기에 새로운 프로퍼티 추가, 주석을 잘 달아주세요
} as const;

type PureInputVariantKey = keyof typeof VARIANT_KLASS_MAP;

export const pureInputVariant = Object.fromEntries(
  Object.entries(VARIANT_KLASS_MAP).map(([key, variant]) => [key, variant]),
) as Record<PureInputVariantKey, PureInputVariantKey>;

/* PureInput.module.scss 에서 정의한 클래스를 외부에서 사용하고 싶을 경우 여기에 추가 */
const KLASS_MAP = {
  error: klass.error,
} as const;

type PureInputKlassKey = keyof typeof KLASS_MAP;

export const pureInputKlass = Object.fromEntries(
  Object.entries(KLASS_MAP).map(([key, klass]) => [key, klass]),
) as Record<PureInputKlassKey, PureInputKlassKey>;

type InputType = 'text' | 'password' | 'email' | 'tel' | 'url' | 'number';

export type PureInputType = {
  getName(): string;
  getValue(): string;
  setValue(value: number | string): void;
  setKlass(klass: string): PureInputType;
  addKlass(klass: string): PureInputType;
  removeKlass(klass: string): PureInputType;
  setFontKlass(font_klass: ReturnType<typeof font>): PureInputType;
  getIndex(): number;
};

// 기본 공통 속성
interface BaseInputProps {
  variant: PureInputVariantKey;
  name: string;
  type?: InputType;
  value?: string | number;
  placeholder?: string;
  maxlength?: number;
  readonly?: boolean;
  disabled?: boolean;
  required?: boolean;
  autofocus?: boolean;
  autocomplete?: boolean;
  klass?: string;
  font_klass?: string;
  index?: number;
  onInput?: (view: PureInputType) => void;
  onFocus?: (view: PureInputType) => void;
  onFocusout?: (view: PureInputType) => void;
}

// 고정 높이 textarea
interface FixedTextareaProps extends BaseInputProps {
  rows: number;
  min_rows?: never;
  max_rows?: never;
}

// 가변 높이 textarea
interface AutoResizeTextareaProps extends BaseInputProps {
  rows?: never;
  min_rows?: number;
  max_rows?: number;
}

// 일반 input
interface InputOnlyProps extends BaseInputProps {
  rows?: never;
  min_rows?: never;
  max_rows?: never;
}

export type InputProps = FixedTextareaProps | AutoResizeTextareaProps | InputOnlyProps;

export class PureInput extends View<InputProps> {
  type: InputType = 'text';
  value: number | string;
  private previousValue = '';
  private readonly minRows: number;
  private readonly maxRows: number;
  private readonly initialRows: number;
  private readonly isAutoResize: boolean;

  constructor(data: InputProps) {
    super(data);
    this.type = data.type ?? this.type;
    this.value = data.value ?? '';
    this.previousValue = this.value.toString();

    // 타입 가드를 통해 어떤 모드인지 확인
    if ('rows' in data && data.rows) {
      // 고정 높이 textarea 모드
      this.isAutoResize = false;
      this.minRows = data.rows;
      this.maxRows = data.rows;
      this.initialRows = data.rows;
    } else if ('min_rows' in data || 'max_rows' in data) {
      // 가변 높이 textarea 모드
      this.isAutoResize = true;
      this.minRows = data.min_rows ?? 3;
      this.maxRows = data.max_rows ?? 10;
      this.initialRows = data.min_rows ?? 3;
    } else {
      // 일반 input 모드
      this.isAutoResize = false;
      this.minRows = 3;
      this.maxRows = 3;
      this.initialRows = 3;
    }
  }

  protected override template() {
    const { placeholder, name, maxlength, readonly, required, autofocus, disabled, autocomplete } = this.data;

    // textarea 사용 여부 확인 - 실제 값이 있을 때만 true
    const useTextarea =
      ('rows' in this.data && this.data.rows) ||
      ('min_rows' in this.data && this.data.min_rows) ||
      ('max_rows' in this.data && this.data.max_rows);

    return html`
      <div class="${this.data.variant} ${this.data.klass ?? ''}">
        ${useTextarea
          ? html`
              <textarea
                class="${klass.pure} ${this.data.font_klass ?? font('14_medium')}"
                type="${this.type}"
                placeholder="${placeholder ?? ''}"
                name="${name}"
                rows="${this.initialRows}"
                ${maxlength && maxlength > 0 ? `maxlength=${maxlength}` : ''}
                ${required ? 'required' : ''}
                ${autofocus ? 'autofocus' : ''}
                ${readonly || disabled ? 'disabled' : ''}
                autocomplete="${autocomplete ? 'on' : 'off'}"
                data-readonly="${!!readonly}"
                style="${this.isAutoResize ? 'resize: none; overflow: hidden;' : ''}"
              >
${this.value}</textarea
              >
            `
          : html`
              <input
                class="${klass.pure} ${this.data.font_klass ?? font('14_medium')}"
                type="${this.type}"
                placeholder="${placeholder ?? ''}"
                value="${this.value}"
                name="${name}"
                ${maxlength && maxlength > 0 ? `maxlength=${maxlength}` : ''}
                ${required ? 'required' : ''}
                ${autofocus ? 'autofocus' : ''}
                ${readonly || disabled ? 'disabled' : ''}
                autocomplete="${autocomplete ? 'on' : 'off'}"
                data-readonly="${!!readonly}"
              />
            `}
      </div>
    `;
  }

  override onRender() {
    const selector = 'input,textarea';

    this.delegate('input', selector, (e) => {
      const target = e.target as HTMLInputElement | HTMLTextAreaElement;
      const newValue = target.value;

      if (this.data.maxlength && newValue.length > this.data.maxlength) {
        // maxlength를 초과하면 이전 값으로 되돌림
        target.value = this.previousValue;
        this.value = this.previousValue;
        return;
      }

      // 값 업데이트
      this.value = newValue;
      this.previousValue = newValue;

      // textarea 높이 자동 조정
      if (target.tagName === 'TEXTAREA' && this.isAutoResize) {
        this.adjustTextareaHeight(target as HTMLTextAreaElement);
      }

      // 항상 onChange 호출
      if (this.data.onInput) {
        this.data.onInput(this);
      }
    })
      .delegate('compositionend', selector, (e) => {
        const target = e.target as HTMLInputElement | HTMLTextAreaElement;

        // maxlength 추가 검사 (조합이 완료된 후)
        if (this.data.maxlength && target.value.length > this.data.maxlength) {
          // maxlength를 초과하면 maxlength에 맞게 잘라냄
          target.value = target.value.substring(0, this.data.maxlength);
          this.value = target.value;
          this.previousValue = target.value;

          // 값이 조정된 경우 onChange 호출
          if (this.data.onInput) {
            this.data.onInput(this);
          }
        }

        // textarea 높이 자동 조정
        if (target.tagName === 'TEXTAREA' && this.isAutoResize) {
          this.adjustTextareaHeight(target as HTMLTextAreaElement);
        }
      })
      .delegate('focus', selector, () => {
        this.data.onFocus && this.data.onFocus(this);
      })
      .delegate('focusout', selector, () => {
        this.data.onFocusout && this.data.onFocusout(this);
      });

    // 초기 렌더링 시 textarea 높이 조정
    const textarea = this.element().querySelector('textarea') as HTMLTextAreaElement;
    if (textarea && this.isAutoResize) {
      // 다음 프레임에서 실행하여 DOM이 완전히 렌더링된 후 조정
      setTimeout(() => {
        this.adjustTextareaHeight(textarea);
      }, 0);
    }
  }

  private adjustTextareaHeight(textarea: HTMLTextAreaElement) {
    if (!this.isAutoResize) return;

    const minRows = this.minRows;
    const maxRows = this.maxRows;

    // 높이 초기화
    textarea.style.height = 'auto';

    // 현재 내용에 맞는 높이 계산
    const style = getComputedStyle(textarea);
    const lineHeight = parseInt(style.lineHeight) || 20;
    const paddingTop = parseInt(style.paddingTop) || 0;
    const paddingBottom = parseInt(style.paddingBottom) || 0;
    const borderTop = parseInt(style.borderTopWidth) || 0;
    const borderBottom = parseInt(style.borderBottomWidth) || 0;

    const scrollHeight = textarea.scrollHeight;
    const minHeight = lineHeight * minRows + paddingTop + paddingBottom + borderTop + borderBottom;
    const maxHeight = lineHeight * maxRows + paddingTop + paddingBottom + borderTop + borderBottom;

    // 최소/최대 높이 범위 내에서 조정
    const newHeight = Math.max(minHeight, Math.min(maxHeight, scrollHeight));
    textarea.style.height = `${newHeight}px`;

    // 스크롤 표시 여부 결정
    if (scrollHeight > maxHeight) {
      textarea.style.overflowY = 'auto';
    } else {
      textarea.style.overflowY = 'hidden';
    }
  }

  getName() {
    return this.data.name;
  }

  getValue() {
    const input = this.element().querySelector<HTMLInputElement | HTMLTextAreaElement>('input, textarea');
    if (input) {
      this.value = input.value;
    }
    return this.value.toString();
  }

  setValue(value: number | string): this {
    const input = this.element().querySelector<HTMLInputElement | HTMLTextAreaElement>('input, textarea');
    if (input) {
      input.value = value.toString();
      this.value = input.value;
      this.previousValue = this.value.toString();

      // textarea 높이 자동 조정
      if (input.tagName === 'TEXTAREA' && this.isAutoResize) {
        this.adjustTextareaHeight(input as HTMLTextAreaElement);
      }
    }
    return this;
  }

  setKlass(klass: string): this {
    this.data.klass = klass;
    this.redraw();
    return this;
  }

  setFontKlass(font_klass: ReturnType<typeof font>): this {
    this.data.font_klass = font_klass;
    this.redraw();
    return this;
  }

  addKlass(klass: string): this {
    this.element().classList.add(klass);
    return this;
  }

  removeKlass(klass: string): this {
    this.element().classList.remove(klass);
    return this;
  }

  getIndex() {
    return this.data.index ?? 0;
  }
}
